import React, { useEffect, useMemo, useState } from "react";
import { TextField, TextFieldProps } from "@mui/material";

type Props = Omit<TextFieldProps, "value" | "onChange" | "type"> & {
  value: number;
  onValueChange: (next: number) => void;

  /** If true, user can leave it blank; on blur it will commit fallbackValue (default 0) */
  allowEmpty?: boolean;

  /** Value used when allowEmpty and the user leaves it blank (default 0) */
  fallbackValue?: number;

  /** Minimum allowed (optional) */
  min?: number;

  /** Maximum allowed (optional) */
  max?: number;

  /** If set (e.g. 2), rounds on commit */
  decimals?: number;

  /** If true, clearing a 0 field on focus for easy typing */
  clearZeroOnFocus?: boolean;
};

function clamp(n: number, min?: number, max?: number) {
  if (typeof min === "number") n = Math.max(min, n);
  if (typeof max === "number") n = Math.min(max, n);
  return n;
}

function roundTo(n: number, decimals?: number) {
  if (typeof decimals !== "number") return n;
  const p = Math.pow(10, decimals);
  return Math.round(n * p) / p;
}

export default function NumericTextField({
  value,
  onValueChange,
  allowEmpty = false,
  fallbackValue = 0,
  min,
  max,
  decimals,
  clearZeroOnFocus = true,
  onBlur,
  onKeyDown,
  onFocus,
  inputProps,
  ...rest
}: Props) {
  const [draft, setDraft] = useState<string>(String(value));

  // Keep draft in sync when parent value changes externally (e.g. Apply to All)
  useEffect(() => {
    setDraft(String(value));
  }, [value]);

  const commit = () => {
    const raw = draft.trim();

    if (raw === "") {
      if (allowEmpty) {
        onValueChange(clamp(roundTo(fallbackValue, decimals), min, max));
        setDraft(String(clamp(roundTo(fallbackValue, decimals), min, max)));
      } else {
        // If not allowed empty, revert to current value
        setDraft(String(value));
      }
      return;
    }

    const next = Number(raw);
    if (Number.isNaN(next)) {
      setDraft(String(value));
      return;
    }

    const fixed = clamp(roundTo(next, decimals), min, max);
    onValueChange(fixed);
    setDraft(String(fixed));
  };

  return (
    <TextField
      {...rest}
      type="number"
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onFocus={(e) => {
        // select all for easy replacement
        e.target.select();

        // if currently 0, clear so user can type immediately
        if (clearZeroOnFocus && value === 0) setDraft("");

        onFocus?.(e);
      }}
      onBlur={(e) => {
        commit();
        onBlur?.(e);
      }}
      onKeyDown={(e) => {
        if (e.key === "Enter") {
          e.preventDefault();
          commit();
        }
        if (e.key === "Escape") {
          e.preventDefault();
          setDraft(String(value));
        }
        onKeyDown?.(e);
      }}
      inputProps={{
        ...(inputProps as any),
        inputMode: "numeric",
      }}
    />
  );
}
